import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Typography, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useEditMode } from './EditModeContext';

const EMPTY_COMPANY_FORM = { label: '', state_code: '', state_id: '', status: '', incorporation_date: '', registered_agent_name: '', registered_agent_address: '', oc_url: '' };
const EMPTY_PERSON_FORM = { label: '', role: '' };
const EMPTY_EDGE_FORM = { source: '', target: '', label: '', confidence: 100 };

const FormDialog = ({ open, onClose, title, onSubmit, children }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
      {children}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button onClick={onSubmit} variant="contained" size="small">Add</Button>
    </DialogActions>
  </Dialog>
);

export const GraphEditToolbar = ({ graphData, entity, ubos, onUpdate, onCombinedUpdate }) => {
  const { isEditMode, addChangeDescription } = useEditMode();
  const [companyOpen, setCompanyOpen] = useState(false);
  const [personOpen, setPersonOpen] = useState(false);
  const [edgeOpen, setEdgeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState(EMPTY_COMPANY_FORM);
  const [personForm, setPersonForm] = useState(EMPTY_PERSON_FORM);
  const [edgeForm, setEdgeForm] = useState(EMPTY_EDGE_FORM);
  const [deleteNodeId, setDeleteNodeId] = useState('');
  const [deleteEdgeOpen, setDeleteEdgeOpen] = useState(false);
  const [deleteEdgeIdx, setDeleteEdgeIdx] = useState('');
  const [editEdgeOpen, setEditEdgeOpen] = useState(false);
  const [editEdgeIdx, setEditEdgeIdx] = useState('');
  const [editEdgeLabel, setEditEdgeLabel] = useState('');
  const [editEdgeConfidence, setEditEdgeConfidence] = useState(100);
  const [editNodeOpen, setEditNodeOpen] = useState(false);
  const [editNodeId, setEditNodeId] = useState('');
  const [editNodeLabel, setEditNodeLabel] = useState('');
  const [editNodeStatus, setEditNodeStatus] = useState('');

  if (!isEditMode || !graphData) return null;

  const nodes = graphData.nodes || [];
  const edges = graphData.edges || [];

  const setField = (setter) => (field) => (e) =>
    setter(prev => ({ ...prev, [field]: e.target.value }));

  const getMainEntityNodeId = () => {
    if (!entity?.business_entity_id) return null;
    const eid = String(entity.business_entity_id);
    const match = nodes.find(n => n.id === eid || n.id === `company_${eid}`);
    return match?.id || null;
  };

  const handleAddCompany = () => {
    const { label, state_code, state_id, status, incorporation_date, registered_agent_name, registered_agent_address, oc_url } = companyForm;
    if (!label.trim()) return;
    const nodeId = `company_${crypto.randomUUID()}`;
    const newNode = {
      id: nodeId,
      label: label.trim(),
      type: 'COMPANY',
      data: {
        legal_name: label.trim(), state_code, state_id, status, incorporation_date,
        registered_agent: { name: registered_agent_name.trim() || null, address: registered_agent_address.trim() || null },
        oc_url: oc_url.trim() || null,
      },
    };
    const mainEntityNodeId = getMainEntityNodeId();
    const newEdge = mainEntityNodeId
      ? { source: mainEntityNodeId, target: nodeId, label: 'associated_with', type: 'associated_with', confidence: 100 }
      : null;
    const updatedEdges = newEdge ? [...edges, newEdge] : edges;
    onUpdate({ ...graphData, nodes: [...nodes, newNode], edges: updatedEdges });
    addChangeDescription(`Added company node: ${label.trim()}`);
    setCompanyForm(EMPTY_COMPANY_FORM);
    setCompanyOpen(false);
  };

  const handleAddPerson = () => {
    const { label, role } = personForm;
    if (!label.trim()) return;
    const nodeId = `person_${crypto.randomUUID()}`;
    const parts = label.trim().split(' ');
    const first_name = parts[0] || '';
    const last_name = parts.slice(1).join(' ') || '';
    const newNode = {
      id: nodeId,
      label: label.trim(),
      type: 'PERSON',
      data: { first_name, last_name, linkedin: '', role },
    };
    const mainEntityNodeId = getMainEntityNodeId();
    const newEdge = mainEntityNodeId
      ? { source: nodeId, target: mainEntityNodeId, label: role.trim() || 'associated_with', type: 'associated_with', confidence: 100 }
      : null;
    const updatedEdges = newEdge ? [...edges, newEdge] : edges;
    onUpdate({ ...graphData, nodes: [...nodes, newNode], edges: updatedEdges });
    addChangeDescription(`Added person node: ${label.trim()}`);
    setPersonForm(EMPTY_PERSON_FORM);
    setPersonOpen(false);
  };

  const handleAddEdge = () => {
    const { source, target, label, confidence } = edgeForm;
    if (!source || !target || source === target) return;
    const newEdge = {
      source,
      target,
      label: label.trim() || 'associated_with',
      type: 'associated_with',
      confidence: Number(confidence) || 100,
    };
    const srcNode = nodes.find(n => n.id === source);
    const tgtNode = nodes.find(n => n.id === target);
    const srcLabel = srcNode?.label || source;
    const tgtLabel = tgtNode?.label || target;

    // Sync to UBO associated_entities when connecting a person to a company
    const personNode = srcNode?.type === 'PERSON' ? srcNode : (tgtNode?.type === 'PERSON' ? tgtNode : null);
    const companyNode = tgtNode?.type === 'COMPANY' ? tgtNode : (srcNode?.type === 'COMPANY' ? srcNode : null);

    if (onCombinedUpdate && personNode && companyNode && ubos) {
      const personNodeId = personNode.id;
      const companyData = companyNode.data || {};
      const newEntity = {
        business_entity_id: companyNode.id.replace(/^company_/, ''),
        legal_name: companyData.legal_name || companyNode.label || '',
        state_code: companyData.state_code || '',
        state_id: companyData.state_id || '',
        status: companyData.status || '',
        incorporation_date: companyData.incorporation_date || null,
        registered_agent: companyData.registered_agent || { name: null, address: null },
        oc_url: companyData.oc_url || null,
      };

      onCombinedUpdate(prev => {
        const updatedUbos = (prev.ubos || []).map(u => {
          const uboPersonId = `person_${u.node_id}`;
          if (uboPersonId !== personNodeId && u.node_id !== personNodeId) return u;
          const alreadyHas = (u.associated_entities || []).some(e => e.business_entity_id === newEntity.business_entity_id);
          if (alreadyHas) return u;
          return { ...u, associated_entities: [...(u.associated_entities || []), newEntity] };
        });
        const gd = prev.graph_data || {};
        return {
          ubos: updatedUbos,
          graph_data: { ...gd, edges: [...(gd.edges || []), newEdge] },
        };
      });
    } else {
      onUpdate({ ...graphData, edges: [...edges, newEdge] });
    }

    addChangeDescription(`Added edge: ${srcLabel} -> ${tgtLabel} (${newEdge.label})`);
    setEdgeForm(EMPTY_EDGE_FORM);
    setEdgeOpen(false);
  };

  const handleDeleteNode = () => {
    if (!deleteNodeId) return;
    const nodeLabel = nodes.find(n => n.id === deleteNodeId)?.label || deleteNodeId;
    const filteredNodes = nodes.filter(n => n.id !== deleteNodeId);
    const filteredEdges = edges.filter(e => {
      const s = String(e.source?.id || e.source);
      const t = String(e.target?.id || e.target);
      return s !== deleteNodeId && t !== deleteNodeId;
    });
    onUpdate({ ...graphData, nodes: filteredNodes, edges: filteredEdges });
    addChangeDescription(`Deleted node: ${nodeLabel}`);
    setDeleteNodeId('');
    setDeleteOpen(false);
  };

  const handleDeleteEdge = () => {
    const idx = parseInt(deleteEdgeIdx, 10);
    if (isNaN(idx) || idx < 0 || idx >= edges.length) return;
    const edge = edges[idx];
    const srcId = String(edge.source?.id || edge.source);
    const tgtId = String(edge.target?.id || edge.target);
    const srcLabel = nodes.find(n => n.id === srcId)?.label || srcId;
    const tgtLabel = nodes.find(n => n.id === tgtId)?.label || tgtId;
    onUpdate({ ...graphData, edges: edges.filter((_, i) => i !== idx) });
    addChangeDescription(`Deleted edge: ${srcLabel} -> ${tgtLabel} (${edge.label})`);
    setDeleteEdgeIdx('');
    setDeleteEdgeOpen(false);
  };

  const handleEditEdge = () => {
    const idx = parseInt(editEdgeIdx, 10);
    if (isNaN(idx) || idx < 0 || idx >= edges.length) return;
    const updated = edges.map((e, i) => {
      if (i !== idx) return e;
      return { ...e, label: editEdgeLabel, confidence: Number(editEdgeConfidence) || 0 };
    });
    const edge = edges[idx];
    const srcId = String(edge.source?.id || edge.source);
    const tgtId = String(edge.target?.id || edge.target);
    const srcLabel = nodes.find(n => n.id === srcId)?.label || srcId;
    const tgtLabel = nodes.find(n => n.id === tgtId)?.label || tgtId;
    onUpdate({ ...graphData, edges: updated });
    addChangeDescription(`Edited edge ${srcLabel} → ${tgtLabel}: label="${editEdgeLabel}", confidence=${editEdgeConfidence}`);
    setEditEdgeOpen(false);
    setEditEdgeIdx('');
  };

  const handleSelectEdgeForEdit = (idx) => {
    setEditEdgeIdx(idx);
    const edge = edges[parseInt(idx, 10)];
    if (edge) {
      setEditEdgeLabel(edge.label || '');
      setEditEdgeConfidence(edge.confidence ?? 100);
    }
  };

  const handleEditNode = () => {
    if (!editNodeId) return;
    const updated = nodes.map(n => {
      if (n.id !== editNodeId) return n;
      return { ...n, label: editNodeLabel, data: { ...n.data, status: editNodeStatus } };
    });
    onUpdate({ ...graphData, nodes: updated });
    addChangeDescription(`Edited node: ${editNodeLabel}`);
    setEditNodeOpen(false);
    setEditNodeId('');
  };

  const handleSelectNodeForEdit = (id) => {
    setEditNodeId(id);
    const node = nodes.find(n => n.id === id);
    if (node) {
      setEditNodeLabel(node.label || '');
      setEditNodeStatus(node.data?.status || '');
    }
  };

  const cf = setField(setCompanyForm);
  const pf = setField(setPersonForm);
  const ef = setField(setEdgeForm);

  return (
    <>
      <Box sx={{
        display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1, px: 1,
      }}>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setCompanyOpen(true)}>
          Add Company
        </Button>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setPersonOpen(true)}>
          Add Person
        </Button>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setEdgeOpen(true)}>
          Add Edge
        </Button>
        <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setEditNodeOpen(true)} sx={{ borderColor: '#ed6c02', color: '#ed6c02' }}>
          Edit Node
        </Button>
        <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => setEditEdgeOpen(true)} sx={{ borderColor: '#ed6c02', color: '#ed6c02' }}>
          Edit Edge
        </Button>
        <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteOpen(true)}>
          Delete Node
        </Button>
        <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteEdgeOpen(true)}>
          Delete Edge
        </Button>
      </Box>

      {/* Add Company */}
      <FormDialog open={companyOpen} onClose={() => setCompanyOpen(false)} title="Add Company Node" onSubmit={handleAddCompany}>
        <TextField label="Name" value={companyForm.label} onChange={cf('label')} required size="small" placeholder="e.g. Acme Corp LLC" />
        <TextField label="State Code" value={companyForm.state_code} onChange={cf('state_code')} size="small" placeholder="e.g. NY" />
        <TextField label="Company Number" value={companyForm.state_id} onChange={cf('state_id')} size="small" placeholder="e.g. 6439824" />
        <TextField label="Status" value={companyForm.status} onChange={cf('status')} size="small" placeholder="e.g. Active" />
        <TextField label="Incorporation Date" value={companyForm.incorporation_date} onChange={cf('incorporation_date')} size="small" placeholder="YYYY-MM-DD" />
        <TextField label="Registered Agent Name" value={companyForm.registered_agent_name} onChange={cf('registered_agent_name')} size="small" placeholder="e.g. John Smith" />
        <TextField label="Registered Agent Address" value={companyForm.registered_agent_address} onChange={cf('registered_agent_address')} size="small" placeholder="e.g. 123 Main St, New York, NY 10001" />
        <TextField label="OpenCorporates URL" value={companyForm.oc_url} onChange={cf('oc_url')} size="small" placeholder="e.g. https://opencorporates.com/companies/us_ny/6439824" />
      </FormDialog>

      {/* Add Person */}
      <FormDialog open={personOpen} onClose={() => setPersonOpen(false)} title="Add Person Node" onSubmit={handleAddPerson}>
        <TextField label="Name" value={personForm.label} onChange={pf('label')} required size="small" placeholder="e.g. John Smith" />
        <TextField label="Role" value={personForm.role} onChange={pf('role')} size="small" placeholder="e.g. owner, director" />
      </FormDialog>

      {/* Add Edge */}
      <FormDialog open={edgeOpen} onClose={() => setEdgeOpen(false)} title="Add Edge" onSubmit={handleAddEdge}>
        <TextField select label="Source Node" value={edgeForm.source} onChange={ef('source')} size="small" required>
          {nodes.map(n => <MenuItem key={n.id} value={n.id}>{n.label || n.id}</MenuItem>)}
        </TextField>
        <TextField select label="Target Node" value={edgeForm.target} onChange={ef('target')} size="small" required>
          {nodes.map(n => <MenuItem key={n.id} value={n.id}>{n.label || n.id}</MenuItem>)}
        </TextField>
        <TextField label="Label" value={edgeForm.label} onChange={ef('label')} size="small" placeholder="e.g. owner, director" />
        <TextField label="Confidence" type="number" value={edgeForm.confidence} onChange={ef('confidence')} size="small" inputProps={{ min: 0, max: 100 }} />
      </FormDialog>

      {/* Delete Node */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Node</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Deleting a node also removes all its connected edges.
          </Typography>
          <TextField
            select
            label="Select Node"
            value={deleteNodeId}
            onChange={(e) => setDeleteNodeId(e.target.value)}
            size="small"
            fullWidth
          >
            {nodes
              .filter(n => n.id !== entity?.business_entity_id)
              .map(n => <MenuItem key={n.id} value={n.id}>{n.label || n.id} ({n.type})</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteNode} variant="contained" color="error" size="small" disabled={!deleteNodeId}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Edge */}
      <Dialog open={deleteEdgeOpen} onClose={() => setDeleteEdgeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Edge</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <TextField
            select
            label="Select Edge"
            value={deleteEdgeIdx}
            onChange={(e) => setDeleteEdgeIdx(e.target.value)}
            size="small"
            fullWidth
          >
            {edges.map((e, i) => {
              const srcId = String(e.source?.id || e.source);
              const tgtId = String(e.target?.id || e.target);
              const src = nodes.find(n => n.id === srcId)?.label || srcId;
              const tgt = nodes.find(n => n.id === tgtId)?.label || tgtId;
              return <MenuItem key={i} value={String(i)}>{src} → {tgt} ({e.label || 'associated_with'})</MenuItem>;
            })}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteEdgeOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteEdge} variant="contained" color="error" size="small" disabled={deleteEdgeIdx === ''}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit Node */}
      <Dialog open={editNodeOpen} onClose={() => setEditNodeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Node</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            select label="Select Node" value={editNodeId}
            onChange={(e) => handleSelectNodeForEdit(e.target.value)} size="small" fullWidth
          >
            {nodes.map(n => <MenuItem key={n.id} value={n.id}>{n.label || n.id} ({n.type})</MenuItem>)}
          </TextField>
          {editNodeId && (
            <>
              <TextField label="Label" value={editNodeLabel} onChange={(e) => setEditNodeLabel(e.target.value)} size="small" />
              <TextField label="Status" value={editNodeStatus} onChange={(e) => setEditNodeStatus(e.target.value)} size="small" />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNodeOpen(false)}>Cancel</Button>
          <Button onClick={handleEditNode} variant="contained" size="small" disabled={!editNodeId}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Edge */}
      <Dialog open={editEdgeOpen} onClose={() => setEditEdgeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Edge</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            select label="Select Edge" value={editEdgeIdx}
            onChange={(e) => handleSelectEdgeForEdit(e.target.value)} size="small" fullWidth
          >
            {edges.map((e, i) => {
              const srcId = String(e.source?.id || e.source);
              const tgtId = String(e.target?.id || e.target);
              const src = nodes.find(n => n.id === srcId)?.label || srcId;
              const tgt = nodes.find(n => n.id === tgtId)?.label || tgtId;
              return <MenuItem key={i} value={String(i)}>{src} → {tgt} ({e.label || 'associated_with'})</MenuItem>;
            })}
          </TextField>
          {editEdgeIdx !== '' && (
            <>
              <TextField label="Label" value={editEdgeLabel} onChange={(e) => setEditEdgeLabel(e.target.value)} size="small" placeholder="e.g. owner, director" />
              <TextField label="Confidence" type="number" value={editEdgeConfidence} onChange={(e) => setEditEdgeConfidence(e.target.value)} size="small" inputProps={{ min: 0, max: 100 }} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditEdgeOpen(false)}>Cancel</Button>
          <Button onClick={handleEditEdge} variant="contained" size="small" disabled={editEdgeIdx === ''}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GraphEditToolbar;
